import { styled } from '@mui/material/styles';
import { type V2HighlightType } from '@Src/middlewares/CustomPageTypes';
import { ImgixImage } from '@Components/ui/ImgixImage';
import {
  Grid,
  Paper,
  type Theme,
  Typography,
  type TypographyProps,
} from '@mui/material';
import { useTranslation } from '@Src/providers/TranslationProvider';
import { StyledSection } from '../Common/StyledSection';
import { useIsMobile } from '@Src/hooks/useIsMobile';
import useMediaQuery from '@mui/material/useMediaQuery';
import MuiButton from '@Components/ui/MuiButton';
import { useAppSelector } from '@Src/hooks/useAppSelector';
import { Link } from '@Components/RoutesUtils';
import { useDesignSchema } from '@Src/providers/DesignSchemaProvider';

type StyledPaperPropsType = {
  ownerState: {
    hasBackground: boolean;
    orientation: 'left' | 'right';
  };
};

const StyledPaper = styled(Paper)<StyledPaperPropsType>`
  border-radius: 0;
  box-shadow: none;
  background-color: ${({ theme, ownerState }) =>
    ownerState?.hasBackground ? theme.palette.primary.light : 'none'};
  padding: ${({ theme }) => theme.spacing(2)};
  padding-top: 0;
  padding-bottom: ${({ theme }) => theme.spacing(3)};
  display: 'flex';
  align-items: 'center';
  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-left: ${({ theme, ownerState }) =>
      ownerState?.orientation === 'right'
        ? theme.spacing(6)
        : theme.spacing(10)};
    margin-right: ${({ theme }) => theme.spacing(6)};
  }
  ${({ theme }) => theme.breakpoints.up('sm')} {
    margin-left: ${({ theme, ownerState }) =>
      ownerState?.orientation === 'right'
        ? theme.spacing(6)
        : theme.spacing(10)};
  }
  ${({ theme }) => theme.breakpoints.down('sm')} {
    ${({ theme }) => ({
      paddingTop: `${theme.spacing(1)} !important`,
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    })}
  }
`;

type StyledImagePropsType = {
  orientation: 'left' | 'right';
  onlyImage?: boolean;
};

const StyledImage = styled('div')<StyledImagePropsType>`
  position: relative;
  z-index: 1;
  ${({ theme }) => theme.breakpoints.up('sm')} {
    left: ${({ theme, orientation, onlyImage }) =>
      orientation === 'right' && !onlyImage
        ? theme.spacing(2.5)
        : 'inherit'} !important;
    right: ${({ theme, orientation, onlyImage }) =>
      orientation === 'left' && !onlyImage
        ? theme.spacing(6)
        : 'inherit'} !important;
    top: ${({ theme }) => theme.spacing(4)} !important;
  }
  ${({ theme }) => theme.breakpoints.down('sm')} {
    left: ${({ orientation, onlyImage }) =>
      orientation === 'right' && !onlyImage ? 0 : 'inherit'} !important;
    right: ${({ orientation, onlyImage }) =>
      orientation === 'left' && !onlyImage ? 0 : 'inherit'} !important;
    margin-top: ${({ theme }) => theme.spacing(4.5)} !important;
  }
`;

const StyledImgixImage = styled(ImgixImage)`
  height: auto !important;
  width: 100%;
`;

const StyledImageGrid = styled(Grid)`
  padding: 0px !important;
  margin: 0px !important;
  height: 100%;
`;

const StyledSubtitleWrapper = styled(Grid)`
  padding-top: 0 !important;
`;

type TypographyWithComponent = React.ComponentType<
  TypographyProps<'span', { component: 'span' }>
>;

const StyledText: TypographyWithComponent = styled(Typography)`
  pre {
    white-space: pre-wrap;
    text-wrap: inherit;
    font: inherit;
    color: inherit;
    font-size: inherit;
    font-family: inherit;
    margin: 0;
  }
  a {
    color: ${({ theme }) => theme.palette.primary.main};
    &:hover {
      color: ${({ theme }) => theme.palette.action.active};
    }
    &:focus {
      text-decoration: none;
      color: ${({ theme }) => theme.palette.action.active};
    }
  }
`;

const StyledOrderButton = styled(MuiButton)(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: `${theme.spacing(2.5)} ${theme.spacing(4)}`,
  textTransform: 'none',
  minWidth: '211px',
}));

type StyledTextGridPropsType = {
  orientation: 'left' | 'right';
  ownerState: {
    centerText?: boolean;
  };
};

const StyledTextGrid = styled(Grid)<StyledTextGridPropsType>`
  justify-content: ${({ orientation, ownerState }) =>
    !ownerState?.centerText && orientation === 'right'
      ? 'flex-end'
      : 'flex-start'} !important;
  padding-top: ${({ theme }) => theme.spacing(4)};
  ${({ theme }) => theme.breakpoints.up('md')} {
    padding-bottom: ${({ theme }) => theme.spacing(4)};
  }
  ${({ theme }) => theme.breakpoints.down('md')} {
    padding-top: 0;
  }

  ${({ theme }) => theme.breakpoints.up('sm')} {
    padding-left: ${({ theme, orientation, ownerState }) =>
      !ownerState?.centerText && orientation === 'right'
        ? theme.spacing(4)
        : 'unset'};
  }
  ${({ theme }) => theme.breakpoints.up('sm')} {
    padding-right: ${({ theme, orientation, ownerState }) =>
      !ownerState?.centerText && orientation === 'left'
        ? theme.spacing(4)
        : 'unset'};
  }
  ${({ theme }) => theme.breakpoints.only('sm')} {
    padding-right: ${({ theme, ownerState }) =>
      !ownerState?.centerText ? theme.spacing(4) : 'unset'};
  }
`;

const StyledHeaderContainer = styled(Grid)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(2),
}));

export const Highlight = ({
  className = '',
  data,
  orientation = 'left',
  hasBackground = true,
}: {
  className?: string;
  data: V2HighlightType;
  orientation?: 'left' | 'right';
  hasBackground?: boolean;
}): JSX.Element => {
  const { translate } = useTranslation();
  const { imageAttributes } = useDesignSchema('v2');
  const hasImage = !!data?.images?.[0];
  const isMobile = useIsMobile();
  const isMd = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const orderUrl = useAppSelector((state) => state.orderUrl.path) || '/order';

  let imageGridOrder;
  let textGridOrder;
  if (orientation === 'left' || isMobile || isMd) {
    imageGridOrder = { xs: 2, sm: 2, md: 1 };
    textGridOrder = { xs: 1, sm: 1, md: 2 };
  } else {
    imageGridOrder = { xs: 1, sm: 1, md: 2 };
    textGridOrder = { xs: 2, sm: 2, md: 1 };
  }

  const showTextSection = !!(data?.description || data?.actionButton?.url);
  const centered = !hasImage || !showTextSection;
  const imageSrc = data?.images?.[0];
  return (
    <StyledSection className={className}>
      <StyledPaper elevation={3} ownerState={{ hasBackground, orientation }}>
        <Grid
          container
          justifyContent={orientation === 'right' ? 'flex-end' : 'inherit'}
        >
          {hasImage && (
            <StyledImageGrid
              item
              xs={12}
              md={showTextSection || !centered ? 6 : 12}
              order={imageGridOrder}
            >
              <StyledImage
                onlyImage={!showTextSection}
                orientation={orientation}
              >
                <StyledImgixImage
                  key={'hightlight-image'}
                  data-fd={'main-image'}
                  src={`${imageSrc}?w=${showTextSection ? 600 : 1200}`}
                  alt={
                    imageAttributes?.[imageSrc]?.alt || `main highlight image`
                  }
                />
              </StyledImage>
            </StyledImageGrid>
          )}
          {showTextSection && (
            <Grid
              item
              xs={12}
              md={hasImage || !centered ? 6 : 12}
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
              textAlign={centered ? 'center' : 'left'}
              order={textGridOrder}
            >
              <StyledTextGrid
                container
                orientation={orientation}
                ownerState={{ centerText: centered }}
              >
                <StyledHeaderContainer item xs={12}>
                  {data?.tag && (
                    <Grid item xs={12}>
                      <Typography variant="body1Medium">
                        {translate(data?.tag)}
                      </Typography>
                    </Grid>
                  )}
                  {data?.title && (
                    <StyledSubtitleWrapper item xs={12}>
                      <Typography variant="h2Display">
                        {translate(data.title)}
                      </Typography>
                    </StyledSubtitleWrapper>
                  )}
                </StyledHeaderContainer>
                {data?.description && (
                  <Grid item xs={12}>
                    <StyledText
                      component="span"
                      data-fd="highlight-description"
                      dangerouslySetInnerHTML={{
                        __html: data.description,
                      }}
                    />
                  </Grid>
                )}
                {data?.actionButton?.url && (
                  <Grid item xs={12}>
                    <Link to={data.actionButton?.url || orderUrl}>
                      <StyledOrderButton variant="contained">
                        {translate(
                          data.actionButton?.label || 'Start_Your_Order',
                        )}
                      </StyledOrderButton>
                    </Link>
                  </Grid>
                )}
              </StyledTextGrid>
            </Grid>
          )}
        </Grid>
      </StyledPaper>
    </StyledSection>
  );
};

export default Highlight;
